import { API_BASE } from './config';

export class ApiException extends Error {
  readonly code: string;
  readonly status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'ApiException';
    this.code = code;
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch {
    throw new ApiException('ネットワークエラーが発生しました', 'network_error', 0);
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const body = (data ?? {}) as { error?: string; message?: string };
    throw new ApiException(body.message || res.statusText || 'リクエストに失敗しました', body.error || 'error', res.status);
  }
  return data as T;
}

export interface InitiateFileInput {
  name: string;
  size: number;
  contentType?: string;
}

export interface InitiateRequest {
  files: InitiateFileInput[];
  retentionDays: number;
  password?: string;
  allowedEmails?: string[];
  emailAuthRequired?: boolean;
  notifyShareUrl?: boolean;
  notifyPassword?: boolean;
  mailSender?: string;
  mailItem?: string;
}

export interface InitiateResponse {
  transferId: string;
  manageToken: string;
  expiresAt: number;
  retentionDays: number;
  chunkSize: number;
  requiresPassword: boolean;
  requiresEmail: boolean;
  notifiesShareUrl: boolean;
  notifiesPassword: boolean;
  files: { fileId: string; name: string; size: number }[];
}

export interface TransferMetadata {
  id: string;
  status: string;
  createdAt: number;
  expiresAt: number;
  requiresPassword: boolean;
  requiresEmail: boolean;
  totalSize: number;
  files: { id: string; name: string; size: number; contentType: string | null }[];
}

export interface GateResponse {
  session: string;
  passwordVerified?: boolean;
  emailVerified?: boolean;
  ready: boolean;
}

const jsonHeaders = { 'content-type': 'application/json' };

export function initiateTransfer(body: InitiateRequest): Promise<InitiateResponse> {
  return request('/transfers', { method: 'POST', headers: jsonHeaders, body: JSON.stringify(body) });
}

export function uploadPart(
  transferId: string,
  fileId: string,
  partNumber: number,
  manageToken: string,
  chunk: Blob,
): Promise<{ partNumber: number; etag: string; size: number }> {
  return request(`/transfers/${transferId}/files/${fileId}/parts/${partNumber}`, {
    method: 'PUT',
    headers: { authorization: `Bearer ${manageToken}` },
    body: chunk,
  });
}

export function completeFile(
  transferId: string,
  fileId: string,
  manageToken: string,
): Promise<{ fileId: string; status: string; size: number }> {
  return request(`/transfers/${transferId}/files/${fileId}/complete`, {
    method: 'POST',
    headers: { authorization: `Bearer ${manageToken}` },
  });
}

export interface CompleteTransferOptions {
  password?: string;
  notifyShareUrl?: boolean;
  notifyPassword?: boolean;
}

export function completeTransfer(
  transferId: string,
  manageToken: string,
  options: CompleteTransferOptions = {},
): Promise<{ id: string; status: string; expiresAt: number; totalSize: number }> {
  const hasBody =
    options.password !== undefined ||
    options.notifyShareUrl !== undefined ||
    options.notifyPassword !== undefined;
  const headers = hasBody
    ? { ...jsonHeaders, authorization: `Bearer ${manageToken}` }
    : { authorization: `Bearer ${manageToken}` };
  return request(`/transfers/${transferId}/complete`, {
    method: 'POST',
    headers,
    body: hasBody ? JSON.stringify(options) : undefined,
  });
}

export function getMetadata(transferId: string): Promise<TransferMetadata> {
  return request(`/transfers/${transferId}`);
}

export function verifyPassword(transferId: string, password: string, session?: string): Promise<GateResponse> {
  return request(`/transfers/${transferId}/verify-password`, {
    method: 'POST',
    headers: session ? { ...jsonHeaders, 'x-download-session': session } : jsonHeaders,
    body: JSON.stringify({ password }),
  });
}

export function requestCode(
  transferId: string,
  email: string,
): Promise<{ status: string; message: string }> {
  return request(`/transfers/${transferId}/request-code`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ email }),
  });
}

export function verifyCode(
  transferId: string,
  email: string,
  code: string,
  session?: string,
): Promise<GateResponse> {
  return request(`/transfers/${transferId}/verify-code`, {
    method: 'POST',
    headers: session ? { ...jsonHeaders, 'x-download-session': session } : jsonHeaders,
    body: JSON.stringify({ email, code }),
  });
}

export function downloadUrl(transferId: string, fileId: string, session?: string): string {
  const base = `${API_BASE}/transfers/${transferId}/files/${fileId}/download`;
  return session ? `${base}?session=${encodeURIComponent(session)}` : base;
}
